import React, { useEffect, useRef, useState } from 'react';
import Axios from 'axios';
import { getAuthHeader } from '../../utils/authToken';
import {
    Alert,
    Button,
    CircularProgress,
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';

export const MATH_ANSWER_SPEC_TYPES = [
    ['number', 'Number'],
    ['expression', 'Expression'],
    ['equation', 'Equation'],
    ['ordered_pair', 'Ordered pair'],
    ['interval', 'Interval'],
    ['inequality', 'Inequality'],
];

export const MATH_REQUIRED_FORMS = [
    ['', 'No required form'],
    ['factored', 'Factored'],
    ['expanded', 'Expanded'],
    ['solved', 'Solved for variable'],
    ['exact', 'Exact form'],
    ['rounded', 'Rounded decimal'],
];

export const MATH_FUNCTION_LABEL_POLICIES = [
    ['required', 'Require the equation label'],
    ['optional_y', 'Allow bare expression or y='],
    ['optional_function_or_y', 'Allow bare expression, y=, or f(x)='],
];

const defaultSpec = {
    version: 1,
    type: 'expression',
    variables: ['x'],
    requiredForm: null,
    tolerance: null,
};

const MathAnswerSpecEditor = ({ config, onChange, questionContent, passage }) => {
    const [suggesting, setSuggesting] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const latest = useRef(null);
    const pending = useRef(null);
    const snapshot = JSON.stringify({ questionContent, passage, config });
    latest.current = snapshot;
    useEffect(() => () => pending.current?.abort(), []);

    const suggestSpec = async () => {
        if (pending.current) return;
        const controller = new AbortController();
        pending.current = controller;
        const requestedSnapshot = snapshot;
        setSuggesting(true);
        setFeedback(null);
        try {
            const { data } = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/hw-questions/suggest-typed-spec`,
                {
                    question_content: questionContent,
                    passage: passage || null,
                    question_type: 'short_answer',
                    answer_tool: 'mathlive',
                    answer_config: { allowTextInput: config?.allowTextInput === true },
                },
                { headers: { Authorization: getAuthHeader() }, signal: controller.signal },
            );
            if (controller.signal.aborted) return;
            if (latest.current !== requestedSnapshot) {
                setFeedback({ severity: 'info', text: 'The question or settings changed. Request a new suggestion for the current question.' });
            } else if (data.typed_spec) {
                onChange({ ...(config || {}), spec: data.typed_spec });
                setFeedback({ severity: 'success', text: 'AI suggestion added. Review it before saving the question.' });
            } else {
                setFeedback({ severity: 'info', text: 'AI could not suggest a specification confidently. Your existing settings have been kept; you can edit them manually.' });
            }
        } catch (error) {
            if (!controller.signal.aborted) {
                setFeedback({ severity: 'error', text: error.response?.data?.error || 'Could not get a suggestion. Please try again.' });
            }
        } finally {
            if (!controller.signal.aborted) {
                pending.current = null;
                setSuggesting(false);
            }
        }
    };
    const spec = config?.spec || null;
    const updateSpec = updates => onChange({
        ...(config || {}),
        spec: { ...spec, ...updates, version: 1 },
    });

    return (
        <Box sx={{ mt: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {questionContent !== undefined && (
                <Box sx={{ mb: 1 }}>
                    <Button
                        type="button"
                        variant="outlined"
                        size="small"
                        disabled={suggesting || !questionContent?.trim() || config?.allowTextInput === true}
                        onClick={suggestSpec}
                        startIcon={suggesting ? <CircularProgress size={16} /> : null}
                    >
                        {suggesting ? 'Suggesting…' : 'Suggest answer spec'}
                    </Button>
                    <Typography variant="caption" display="block" color="text.secondary">
                        {config?.allowTextInput === true
                            ? 'AI specifications are available for math-only answers.'
                            : 'Suggests the answer type and required form from the current question.'}
                    </Typography>
                </Box>
            )}
            {feedback && <Alert severity={feedback.severity} sx={{ mb: 1 }}>{feedback.text}</Alert>}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                    inputProps={{ 'aria-label': 'Use typed answer specification' }}
                    checked={Boolean(spec)}
                    onChange={(event) => onChange({
                        ...(config || {}),
                        spec: event.target.checked ? defaultSpec : null,
                    })}
                    size="small"
                />
                <Box>
                    <Typography variant="body2">Use typed answer specification</Typography>
                    <Typography variant="caption" color="text.secondary">
                        Records the intended answer type now; typed symbolic comparison is enabled separately.
                    </Typography>
                </Box>
            </Box>
            {spec && (
                <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="math-answer-spec-type-label">Answer type</InputLabel>
                        <Select
                            labelId="math-answer-spec-type-label"
                            label="Answer type"
                            value={spec.type || 'expression'}
                            onChange={(event) => updateSpec({ type: event.target.value })}
                        >
                            {MATH_ANSWER_SPEC_TYPES.map(([value, label]) => (
                                <MenuItem key={value} value={value}>{label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        size="small"
                        label="Variables"
                        value={(spec.variables || []).join(', ')}
                        onChange={(event) => updateSpec({
                            variables: event.target.value
                                .split(',')
                                .map(value => value.trim())
                                .filter(Boolean),
                        })}
                        helperText="Comma-separated, e.g. x, y"
                        sx={{ minWidth: 220 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 190 }}>
                        <InputLabel id="math-answer-required-form-label">Required form</InputLabel>
                        <Select
                            labelId="math-answer-required-form-label"
                            label="Required form"
                            value={spec.requiredForm || ''}
                            onChange={(event) => updateSpec({ requiredForm: event.target.value || null })}
                        >
                            {MATH_REQUIRED_FORMS.map(([value, label]) => (
                                <MenuItem key={value || 'none'} value={value}>{label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        size="small"
                        type="number"
                        label="Numeric tolerance"
                        value={spec.tolerance ?? ''}
                        onChange={(event) => updateSpec({
                            tolerance: event.target.value === '' ? null : Number(event.target.value),
                        })}
                        inputProps={{ min: 0, step: 'any' }}
                        helperText="Blank means exact"
                        sx={{ width: 170 }}
                    />
                    {spec.type === 'equation' && (
                        <>
                            <TextField
                                size="small"
                                label="Target variable"
                                value={spec.targetVariable || ''}
                                onChange={(event) => updateSpec({ targetVariable: event.target.value.trim() || null })}
                                helperText="Used by solved form"
                                sx={{ width: 170 }}
                            />
                            <FormControl size="small" sx={{ minWidth: 260 }}>
                                <InputLabel id="math-answer-label-policy-label">Function label</InputLabel>
                                <Select
                                    labelId="math-answer-label-policy-label"
                                    label="Function label"
                                    value={spec.labelPolicy || 'required'}
                                    onChange={(event) => updateSpec({ labelPolicy: event.target.value })}
                                >
                                    {MATH_FUNCTION_LABEL_POLICIES.map(([value, label]) => (
                                        <MenuItem key={value} value={value}>{label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </>
                    )}
                    {spec.requiredForm === 'rounded' && (
                        <>
                            <TextField
                                size="small"
                                type="number"
                                label="Decimal places"
                                value={spec.decimalPlaces ?? ''}
                                onChange={(event) => updateSpec({
                                    decimalPlaces: event.target.value === '' ? null : Number(event.target.value),
                                })}
                                inputProps={{ min: 0, max: 15, step: 1 }}
                                sx={{ width: 150 }}
                            />
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        checked={Boolean(spec.requireTrailingZeros)}
                                        onChange={(event) => updateSpec({
                                            requireTrailingZeros: event.target.checked,
                                        })}
                                    />
                                )}
                                label="Require trailing zeros"
                            />
                        </>
                    )}
                </Box>
            )}
        </Box>
    );
};

MathAnswerSpecEditor.defaultProps = {
    config: {},
};

export default MathAnswerSpecEditor;
